import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { UserDetailComponent } from './detail/user-detail.component';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { DeleteDialog } from '../dialogs/delete.component';
import { UserService } from './user.service';
import { debounceTime, distinctUntilChanged, finalize, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { PaginationResult } from 'src/app/models/common.response';
import { ToastrService } from 'ngx-toastr';
import { MatPaginator } from '@angular/material/paginator';
import { AppBreadcrumbComponent } from "../../layouts/full/shared/breadcrumb/breadcrumb.component";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { FallbackImageDirective } from 'src/app/common/directive/fallback-image.directive';
import { LoaderComponent } from "src/app/shared/loader/loader.component";
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { PaginationRequest } from 'src/app/models/common.request';
import { IUsers } from './iusers';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { MaskSensitivePipe } from 'src/app/pipe/mask-sensitive.pipe';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';

@Component({
  selector: 'app-user',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    FormsModule,
    ReactiveFormsModule,
    AppBreadcrumbComponent,
    FallbackImageDirective,
    TranslateModule,
    LoaderComponent,
    MaskSensitivePipe
  ],
  providers: [DatePipe],
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss'
})
export class UserComponent implements OnInit, AfterViewInit {

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  usercount: number = 0;
  users$: Observable<IUsers[]>;
  usersdata: IUsers[];
  isLoading: boolean = false;
  isLoadData: boolean;
  max_users: number = 5;
  searchuser: FormControl = new FormControl();
  staticimagepath = environment.imagePath;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  displayedColumns: string[] = [
    'fullname',
    'roleName',
    'email',
    'phoneno',
    'username',
    'birthDate',
    'joinDate',
    'panNumber',
    'aadhaarNumber',
    'action'
  ];
  dataSource = new MatTableDataSource<IUsers>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  constructor(
    public dialog: MatDialog,
    private _userService: UserService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private loaderService: LoaderService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this._userService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.usercount = this.pagination.rowCount;
        }
      });

    this._userService.users$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.dataSource = new MatTableDataSource(data); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });

    this.searchuser.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.loaderService.show();
        return this._userService.getAllUsers({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (setTimeout(() => {
            this.loaderService.hide();
          }, 1000)))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.users;
        this.usercount = response.result.pagination.rowCount;
      }
      setTimeout(() => {
        this.loaderService.hide();
      }, 1000)
    });

  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  addUser() {
    this.dialog.open(UserDetailComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
    }).afterClosed().subscribe((response) => {
      if (response) {
        this.pagination.currentPage = 0;
        this.loadUser();
      }
    });
  }

  deleteDialog(id: number) {

    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.USER') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._userService.deleteUser(id).subscribe((res) => {
          if (res.isSuccess) {
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.USER')));
            this.loadUser();
          }
          else {
            const allErrors = Object.values(res.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        })
      }
    });

  }

  editOpenDialog(data: IUsers): void {
    this.dialog.open(UserDetailComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: 'side-panel',
      disableClose: true,
      data: {
        user: data,
        isEdit: true
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.loadUser();
      }
    });
  }

  loadUser(): void {
    this.loaderService.show();
    const globalSearchValue = this.searchuser?.value?.trim();
    const requestPayload: PaginationRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._userService.getAllUsers(requestPayload)
      .pipe(finalize(() => (setTimeout(() => {
        this.loaderService.hide();
      }, 1000))))
      .subscribe((response) => {
        if (response && response.result) {
          const Userslist = response.result.users;
          this.usersdata = Userslist;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(Userslist);
          } else {
            this.dataSource.data = Userslist;
          }
          this.dataSource.paginator = this.paginator;
          this.usercount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

}
